// v2.19.29 — Bloc "Inviter un partenaire" dans la section dorée Owner.
// Génère un slug via /api/invites/generate, ouvre WhatsApp, propose une modale
// "Mes invitations envoyées" avec status (pending / clicked / signed_up).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_LABEL = {
  pending: { emoji: "📤", label: "Envoyée", color: "#94A3B8" },
  clicked: { emoji: "👁", label: "Cliquée", color: "#FBBF24" },
  signed_up: { emoji: "✅", label: "Inscrit(e)", color: "#86EFAC" },
  rejected: { emoji: "❌", label: "Refusée", color: "#FCA5A5" },
};

export default function OwnerInviteBlock() {
  const [targetPhone, setTargetPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const onGenerate = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/invites/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({ target_phone: targetPhone || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        toast.error(data.error || `Erreur ${r.status}`);
        return;
      }
      window.open(data.wa_url, "_blank", "noopener,noreferrer");
      toast.success("✅ Invitation prête, WhatsApp ouvert");
      setTargetPhone("");
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  }, [targetPhone]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API}/api/invites/my-invitations`, { headers: { ..._authHeaders() } });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) setInvites(data.items || []);
      else toast.error(data.error || `Erreur ${r.status}`);
    } catch (_) {
      toast.error("Erreur réseau");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { if (showList) loadList(); }, [showList, loadList]);

  return (
    <div data-testid="owner-invite-block" style={{
      marginTop: 14, paddingTop: 14, borderTop: "1px dashed rgba(251,191,36,0.35)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 18 }}>📨</span>
        <h3 style={{ margin: 0, fontSize: 14, color: "#FBBF24", fontWeight: 700 }}>Inviter un partenaire commerçant</h3>
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.45 }}>
        Envoie une invitation par WhatsApp à un commerçant qui devrait être sur AYA.
        Il créera sa fiche en 2 min via un lien unique de tracking.
      </p>
      <label style={{ display: "block", fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 4 }}>
        Téléphone du partenaire <span style={{ opacity: 0.5 }}>(optionnel, format +225...)</span>
      </label>
      <input
        type="tel"
        value={targetPhone}
        onChange={(e) => setTargetPhone(e.target.value)}
        placeholder="+225 07 08 90 12 34"
        data-testid="owner-invite-phone-input"
        style={{
          width: "100%",
          padding: "9px 12px",
          background: "rgba(15,23,42,0.55)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          color: "#fff",
          fontSize: 13,
          marginBottom: 10,
          fontFamily: "inherit",
          boxSizing: "border-box",
        }}
      />
      <button
        type="button"
        onClick={onGenerate}
        disabled={busy}
        data-testid="owner-invite-generate-btn"
        style={{
          width: "100%",
          padding: "11px 14px",
          background: busy ? "rgba(205,164,82,0.5)" : "linear-gradient(135deg, #F5E9C6 0%, #E4C87F 55%, #CDA452 100%)",
          color: "#3A2E10",
          border: "none",
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 800,
          cursor: busy ? "default" : "pointer",
          boxShadow: "0 4px 14px rgba(205,164,82,0.35)",
          letterSpacing: 0.3,
        }}
      >{busy ? "Génération…" : "📲 Générer & Ouvrir WhatsApp"}</button>
      <button
        type="button"
        onClick={() => setShowList(true)}
        data-testid="owner-invite-list-link"
        style={{
          marginTop: 10, background: "none", border: 0, color: "rgba(232,236,247,0.65)",
          fontSize: 12, textDecoration: "underline", cursor: "pointer", display: "block", marginLeft: "auto",
        }}
      >Voir mes invitations envoyées</button>

      {showList && (
        <InviteListModal
          invites={invites}
          loading={loadingList}
          onClose={() => setShowList(false)}
          onRefresh={loadList}
        />
      )}
    </div>
  );
}

function InviteListModal({ invites, loading, onClose, onRefresh }) {
  return (
    <div
      data-testid="owner-invite-list-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(7,11,24,0.78)",
        backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        zIndex: 9400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        data-testid="owner-invite-list-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 560,
          background: "linear-gradient(155deg, #14223D 0%, #0B132B 100%)",
          border: "1px solid rgba(251,191,36,0.25)", borderRadius: 18,
          padding: "22px 22px 18px", color: "#E8ECF7",
          maxHeight: "85vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "#FBBF24", fontWeight: 700 }}>📋 Mes invitations envoyées</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={onRefresh} disabled={loading} data-testid="owner-invite-list-refresh" style={miniBtnStyle}>
              {loading ? "…" : "↻"}
            </button>
            <button onClick={onClose} data-testid="owner-invite-list-close" style={miniBtnStyle}>✕</button>
          </div>
        </div>
        {loading && <div style={{ fontSize: 13, color: "#94A3B8" }}>Chargement…</div>}
        {!loading && invites.length === 0 && (
          <div data-testid="owner-invite-list-empty" style={{
            padding: "20px 12px", textAlign: "center", color: "#94A3B8", fontSize: 13,
            background: "rgba(255,255,255,0.03)", borderRadius: 10,
          }}>
            Aucune invitation envoyée pour le moment.
          </div>
        )}
        {!loading && invites.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {invites.map((inv) => {
              const meta = STATUS_LABEL[inv.status] || STATUS_LABEL.pending;
              return (
                <div
                  key={inv.invite_slug}
                  data-testid={`invite-row-${inv.invite_slug}`}
                  style={{
                    padding: "10px 12px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10,
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                    <code style={{ fontSize: 11, color: "#93C5FD" }}>{inv.invite_slug}</code>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>
                      {new Date(inv.issued_at).toLocaleDateString()} ·{" "}
                      {inv.target_phone || "lien générique"}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 11, padding: "3px 9px", borderRadius: 999,
                    background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55`,
                    fontWeight: 700, whiteSpace: "nowrap",
                  }}>{meta.emoji} {meta.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const miniBtnStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff",
  width: 32, height: 32, borderRadius: 6,
  cursor: "pointer", fontSize: 14,
};
